<script setup>
// Compact auto-reply switch for the reply composer (REQ-20260812-11).
// Same flag as the counsel stop/resume tools: loaded from the counsel
// endpoint and kept live over ActionCable. Display is the effective AND of
// the conversation flag and the agent×inbox gate (REQ-20260813-02).
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { conversationAssignedTo } from 'custom/dashboard/extensions/ai_agent/inboxAutoReplyStore';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();

const currentChat = computed(() => {
  const selected = store.getters.getSelectedChat;
  if (!selected || String(selected.id) !== String(props.conversationId)) {
    return null;
  }
  return selected;
});
const inboxId = computed(() => currentChat.value?.inbox_id);
const isMine = computed(() =>
  conversationAssignedTo(currentChat.value, store.getters.getCurrentUser?.id)
);
const conversationOn = computed(() =>
  store.getters.counselAutoReplyFor(props.conversationId)
);
const inboxOn = computed(() =>
  store.getters.agentInboxAutoReplyFor(inboxId.value)
);
const inboxGated = computed(() => isMine.value && inboxOn.value === false);
const enabled = computed(() => conversationOn.value && !inboxGated.value);
const flags = computed(() =>
  store.getters.counselUiFlagsFor(props.conversationId)
);

const ensureLoaded = () => {
  if (!props.conversationId) return;
  if (!flags.value.loaded) {
    store.dispatch('fetchCounselThread', props.conversationId).catch(() => {});
  }
  if (inboxId.value) {
    store.dispatch('loadAgentInboxAutoReply', inboxId.value);
  }
};

onMounted(ensureLoaded);
watch(() => props.conversationId, ensureLoaded);
watch(inboxId, id => {
  if (id) store.dispatch('loadAgentInboxAutoReply', id);
});

const toggle = async () => {
  if (flags.value.updatingAutoReply || inboxGated.value) return;
  try {
    await store.dispatch('toggleCounselAutoReply', {
      conversationId: props.conversationId,
      enabled: !conversationOn.value,
    });
  } catch (error) {
    useAlert(t('CUSTOM.COUNSEL.AUTO_REPLY_FAILED'));
  }
};
</script>

<template>
  <button
    type="button"
    class="inline-flex items-center gap-1.5 shrink-0 px-2 py-1 text-xs font-medium transition-colors border rounded-md whitespace-nowrap bg-n-alpha-1 border-n-weak text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-60 disabled:cursor-not-allowed"
    :disabled="flags.updatingAutoReply || inboxGated"
    :aria-pressed="enabled"
    :title="
      inboxGated
        ? t('CUSTOM.COUNSEL.AUTO_REPLY_INBOX_OFF')
        : t('CUSTOM.COUNSEL.AUTO_REPLY_HINT')
    "
    @click="toggle"
  >
    <span
      class="inline-block rounded-full size-1.5"
      :class="enabled ? 'bg-n-brand' : 'bg-n-slate-9'"
    />
    <span>{{ t('CUSTOM.COUNSEL.AUTO_REPLY') }}</span>
    <span class="hidden sm:inline text-n-slate-10">
      {{
        enabled
          ? t('CUSTOM.COUNSEL.AUTO_REPLY_ON')
          : t('CUSTOM.COUNSEL.AUTO_REPLY_OFF')
      }}
    </span>
  </button>
</template>
